// ISDP CUSTOM: `ui:field: DocTypePicker` — the doc-type choice on the starter kit's first page, as
// three selectable cards in a row (stacking on narrow screens) instead of a radio list with a long
// markdown description. Value: the plain string 'quick' | 'guide' | 'bilingual' (the template keeps
// its `enum` for validation). Behaves as a radio group: one tab stop, arrow keys move + select,
// Space/Enter select.
//
// The card copy (title, one-liner, "Best for") lives in the devsecops i18n namespace keyed by value.
// An enum value the frontend doesn't know still renders, titled from the schema's enumNames.
import { useRef, type KeyboardEvent } from 'react';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import type { CustomFieldValidator, FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { FormControl, FormHelperText, FormLabel } from '@material-ui/core';
import { devsecopsMessages } from '../../../i18n/devsecops';
import { validatorMessages } from './fieldMessages';
import { docTypeOptions, type DocTypeOption } from './fieldHelpers';

export const DocTypePickerField = (props: FieldExtensionComponentProps<string>) => {
  const { formData, onChange, schema, rawErrors, required, disabled, idSchema } = props;
  const { t } = useTranslationRef(devsecopsMessages);
  const tt = t as unknown as (k: string) => string;
  const id = idSchema?.$id ?? 'docType';
  const options = docTypeOptions(schema as { enum?: unknown; enumNames?: unknown });
  const selected = typeof formData === 'string' ? formData : '';
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  // Roving tab stop: the selected card, else the first.
  const tabIndexOf = (i: number) => {
    const sel = options.findIndex(o => o.value === selected);
    return i === (sel >= 0 ? sel : 0) ? 0 : -1;
  };

  const pick = (o: DocTypeOption) => {
    if (!disabled) onChange(o.value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = (i + step + options.length) % options.length;
    refs.current[next]?.focus();
    pick(options[next]);
  };

  const text = (o: DocTypeOption, part: 'Title' | 'Desc' | 'Best') => {
    if (o.known) return tt(`fields.docType${o.known}${part}`);
    return part === 'Title' ? o.label : '';
  };

  const showError = (rawErrors?.length ?? 0) > 0;
  return (
    <FormControl component="fieldset" fullWidth margin="normal" required={required} className="devhub-sf">
      {schema.title && (
        <FormLabel component="legend" id={`${id}-label`}>
          {schema.title}
        </FormLabel>
      )}
      <div className="devhub-doctype" role="radiogroup" aria-labelledby={`${id}-label`} id={id}>
        {options.map((o, i) => {
          const checked = o.value === selected;
          const best = text(o, 'Best');
          const desc = text(o, 'Desc');
          return (
            <button
              key={o.value}
              ref={el => { refs.current[i] = el; }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={tabIndexOf(i)}
              disabled={disabled}
              data-value={o.value}
              className={`devhub-doctype__card${checked ? ' is-selected' : ''}`}
              onClick={() => pick(o)}
              onKeyDown={e => onKeyDown(e, i)}
            >
              <span className="devhub-doctype__top">
                <span className="devhub-doctype__icon" aria-hidden="true">
                  <odx-icon name={o.icon} size="md" />
                </span>
                <span className="devhub-doctype__check" aria-hidden="true">
                  {checked && <odx-icon name="core::check" size="sm" />}
                </span>
              </span>
              <span className="devhub-doctype__title">{text(o, 'Title')}</span>
              {desc && <span className="devhub-doctype__desc">{desc}</span>}
              {best && (
                <span className="devhub-doctype__best">
                  <strong>{tt('fields.docTypeBestFor')}</strong> {best}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {showError && !selected && <FormHelperText error>{tt('fields.docTypeRequired')}</FormHelperText>}
    </FormControl>
  );
};

export const docTypePickerValidation: CustomFieldValidator<string> = (value, validation, { apiHolder }) => {
  if (!value) validation.addError(validatorMessages(apiHolder)('fields.docTypeRequired'));
};
